import React, { useRef, useMemo, useState, useEffect, useCallback } from "react";
import { Form, Icon, Schema, Whisper, Tooltip, DatePicker, Alert, Button } from "rsuite";
import { connect } from "react-redux";
import styled from "styled-components";

import { checkPermissionsFor } from "store/storeHelpers";
import { toStartDayUTCTime, resolvePerPage } from "utils";
import {
    ACCOUNT_PRICE_MODIFY_API,
    ACCOUNT_PRICE_REMOVE_API,
    SMS_SUPPLIER_RATE_CREATE_METHOD,
    SMS_SUPPLIER_RATE_MODIFY_LIST_METHOD,
    SMS_SUPPLIER_RATE_REMOVE_LIST_METHOD
} from "const/apiMethods";
import { useWindowWidth } from "hooks";
import { api } from "api/loginRoutes";
import Checkbox from "hoc/Checkbox";
import {
    DEFAULT_PER_PAGE_SMALL,
    SP_KEY__GLOBAL_ACCESS
} from "const";
import { LS_ACCOUNT_RATES_PER_PAGE } from "const/localStorageKeys";
import { FilterSectionFlexboxGrid, StyledItemFitWidth, StyledItemAutoMargin } from "components/base/FitContainers";
import { ButtonGhost, ButtonPrimary, ButtonClose } from "components/base/BaseButton";
import CustomField from "components/Form/CustomField";
import RateField from "components/Form/RateField";
import Modal from "components/Modal";
import ModalConfirm from "components/Modal/ModalConfirm";
import DropdownAddPrices from "./DropdownAddPrices";
import ModalUploadResult from "./ModalUploadResult";
import ModalAddNewPrice from "./ModalAddNewPrice";
import ModalUploadPrice from "./ModalUploadPrice";
import ModalDeletePrice from "./ModalDeletePrice";
import RatesFilters from "./RatesFilters";
import ExportPrice from "./ExportPrice";
import AddNewRate from "./AddNewRate";
import RatesTable from "./RatesTable";
import { de } from "date-fns/locale";

const { NumberType } = Schema.Types;


const TabRates = (
    {
        isActive,
        rates,
        ratesLoading,
        countryList,
        worldzone_list,
        subdestination_list,
        destination_list,
        getItems,
        account,
        service,
        accountId,
        supplierId,

        isSupplier,

        e212List,
        spKey,
        defaultSPKey,
        currency_list,
        payment_terms_list,
        accountServicePlanList,

        isSupervisorSms,

        servicePlanList = [],
        supplierPriceList = [],

        deletePrice,
        syncSupplierPrice,
        getE212DropdownList,
        getSupplierPriceList,
        getReferencesForReports,
        getSupplierAccountTrunk
    }
) => {
    const hasServicePlans = servicePlanList && servicePlanList.length;

    const initRef = useRef(false);

    const perPageLSKey = LS_ACCOUNT_RATES_PER_PAGE;
    const initialPerPage = useMemo(() => resolvePerPage(
        perPageLSKey,
        null,
        DEFAULT_PER_PAGE_SMALL,
        1,
        10000,
    ), [perPageLSKey]);

    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState((rates || []).length);

    const [perPage, onChangePerPage] = useState(initialPerPage);
    const [activePage, onChangePage] = useState(1);
    const [showAddNewRate, setShowAddNewRate] = useState(false);
    const [showAddNewPrice, setShowAddNewPrice] = useState(false);

    const [showDeletePriceModal, setShowDeletePriceModal] = useState(false);

    const [filter, setFilter] = useState({ sp_key: null });
    const [deleteModal, setDeleteModal] = useState(false);
    const [deleteID, setDeleteId] = useState(false);

    const filteredPriceList = useMemo(() => {
        return !service && !isSupplier && hasServicePlans
            ? servicePlanList.filter(servicePlan => servicePlan.sp_key !== SP_KEY__GLOBAL_ACCESS)
            : null;
    }, [service, isSupplier, hasServicePlans, servicePlanList]);

    const [pickedPrice, pickPrice] = useState({});
    const pendingDeletedPriceIdRef = useRef(null);

    const isGlobalPrice = useMemo(() => pickedPrice?.global, [pickedPrice]);

    const windowWidth = useWindowWidth();
    const isMobile = windowWidth < 768;

    const filteredSupplierPriceList = useMemo(() => {
        return supplierPriceList.filter(price => {
            return !price.global || (price.global && price.used);
        });
    }, [supplierPriceList]);

    const pickedPriceId = pickedPrice?.id;

    const notDeletablePrice = useMemo(() => {
        if (!pickedPrice?.global && !pickedPrice?.used) {
            return false;
        }

        return true;
    }, [pickedPrice]);

    useEffect(() => {
        setTotal((rates || []).length);
    }, [rates]);

    useEffect(() => {
        if (!filter.sp_key && spKey) {
            setFilter(prevFilter => ({ ...prevFilter, sp_key: spKey }));
        }
    }, [spKey]);

    useEffect(() => {
        if (!filter.sp_key) return;
        if (isSupplier && !pickedPriceId) return;

        if (isSupplier) {
            if (filter.price_id && filter.price_id !== pickedPriceId) {
                return;
            }

            if (pendingDeletedPriceIdRef.current === pickedPriceId) {
                return;
            }

            getItems(filter, null, pickedPriceId, 1, perPage);
        } else {
            getItems(filter, null, null, 1, perPage);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter, isSupplier, pickedPriceId, perPage]);

    useEffect(() => {
        if (!isSupplier) {
            return;
        }

        const pendingDeletedPriceId = pendingDeletedPriceIdRef.current;

        if (pendingDeletedPriceId) {
            const hasPendingInList = filteredSupplierPriceList.some(price => price.id === pendingDeletedPriceId);

            if (!hasPendingInList) {
                pendingDeletedPriceIdRef.current = null;
            }
        }

        if (!filteredSupplierPriceList.length) {
            if (pickedPriceId) {
                pickPrice({});
            }

            setFilter(prevFilter => {
                if (!prevFilter.price_id) {
                    return prevFilter;
                }

                return { ...prevFilter, price_id: null };
            });

            return;
        }

        const selectedPrice = filteredSupplierPriceList.find(price => price.id === pickedPriceId);
        const nextPickedPrice = selectedPrice || filteredSupplierPriceList.find(price => price.id !== pendingDeletedPriceIdRef.current);

        if (!nextPickedPrice) {
            return;
        }

        if (!selectedPrice) {
            pickPrice(nextPickedPrice);
        }

        setFilter(prevFilter => {
            if (prevFilter.price_id === nextPickedPrice.id) {
                return prevFilter;
            }

            return { ...prevFilter, price_id: nextPickedPrice.id };
        });
    }, [isSupplier, filteredSupplierPriceList, pickedPriceId, pickPrice]);

    useEffect(() => {
        if (!isActive || !isSupplier) {
            return;
        }

        if (supplierId) {
            getSupplierAccountTrunk(supplierId, "transit");
            getSupplierPriceList(supplierId);
        }

        getE212DropdownList({}, true);
        getReferencesForReports();
    }, [isActive, isSupplier, supplierId]);

    const onChangeFilters = (filter) => {
        setFilter(filter);
        onChangePage(1);
    };

    const onClickDelete = (id) => {
        setDeleteId(id);
        setDeleteModal(true)
    };

    const fetchRates = (nextFilter = filter, nextPage = activePage, nextPerPage = perPage, nextPickedPriceId = pickedPriceId) => {
        if (!nextFilter?.sp_key) return;

        if (isSupplier) {
            if (!nextPickedPriceId) return;
            getItems(nextFilter, null, nextPickedPriceId, nextPage, nextPerPage);
            return;
        }

        getItems(nextFilter, null, null, nextPage, nextPerPage);
    };

    const handleChangePage = (nextPage) => {
        onChangePage(nextPage);
        fetchRates(filter, nextPage, perPage, pickedPriceId);
    };


    const onSavePerPage = (nextPerPage) => {
        const safePerPage = parseInt(nextPerPage, 10);

        if (!Number.isFinite(safePerPage)) {
            return;
        }

        const boundedPerPage = Math.min(Math.max(safePerPage, 1), 10000);

        localStorage.setItem(perPageLSKey, boundedPerPage);

        if (boundedPerPage === perPage) {
            fetchRates(filter, 1, boundedPerPage, pickedPriceId);
            onChangePage(1);
            return;
        }

        onChangePerPage(boundedPerPage);
        onChangePage(1);
    };

    const [editModal, setEditModal] = React.useState(false);
    const [rateToEdit, setRateToEdit] = React.useState(null);
    const [editDisabled, setEditDisabled] = React.useState(false);
    const [formRef, setFormRef] = React.useState(null);

    const [showUploadRate, setShowUploadRate] = useState(false);
    const [showUploadRateResult, setShowUploadRateResult] = useState(false);
    const [dataUploadRateResult, setDataUploadRateResult] = useState(false);

    const onClickEdit = (rate) => {
        setRateToEdit(rate);
        setEditModal(true);
    };

    const onDeleteId = async (data) => {
        setDeleteModal(false);
        setLoading(true);
        const method = isSupplier ? SMS_SUPPLIER_RATE_REMOVE_LIST_METHOD : ACCOUNT_PRICE_REMOVE_API;
        const params = isSupplier ? {
            target: {
                "sms.supplier_price_id": pickedPrice?.id
            },
            "sms.supplier_price_mccmnc_key_list": [deleteID],
            ...data
        } : {
            target: {
                account_id: accountId,
                account_price_id: deleteID
            },
            ...data
        };

        const result = await api(method, params);

        setLoading(false);

        if (result) {
            return getItems(filter, null, pickedPrice?.id);
        }
    };

    const onEditId = async () => {
        if (formRef.check()) {
            const data = { ...formRef.getFormValue() };
            setEditDisabled(true);

            if (data.withDate) {
                data.start_date = data.start_date ? toStartDayUTCTime(new Date(data.start_date)) : null;
            } else {
                delete data.start_date;
            }

            delete data.withDate;
            if (data.hasOwnProperty("end_date"))
                delete data.end_date;

            const method = isSupplier ? SMS_SUPPLIER_RATE_MODIFY_LIST_METHOD : ACCOUNT_PRICE_MODIFY_API;
            const params = isSupplier ? {
                target: {
                    "sms.supplier_price_id": pickedPrice?.id
                },
                "sms.supplier_price_mccmnc_key_list": [rateToEdit?.key],
                ...data,
                rate: +formRef.getFormValue().rate
            } : {
                target: {
                    account_id: accountId,
                    account_price_id: rateToEdit.id
                },
                ...data,
                rate: +formRef.getFormValue().rate
            };

            const result = await api(method, params);

            setEditDisabled(false);

            if (result) {
                setEditModal(false);
                getItems(filter, null, pickedPrice?.id);
            }
        }
    };

    const onDeletePrice = useCallback(() => {
        if (!pickedPrice?.id) return;

        pendingDeletedPriceIdRef.current = pickedPrice.id;

        deletePrice(pickedPrice.id)
            .then((response) => {
                if (response["sms.supplier_price_id"]) {
                    pickPrice({});
                }
            })
            .finally(() => {
                getSupplierPriceList(supplierId);
            })
    }, [deletePrice, getSupplierPriceList, pickPrice, pickedPrice, supplierId]);

    const accountPaymentTermList = payment_terms_list.filter(item => item.pt_key === account.pt_key);

    const showAddPriceButton = !isSupervisorSms || isSupervisorSms && checkPermissionsFor(SMS_SUPPLIER_RATE_CREATE_METHOD);

    const synchronizationMessage = `Last synchronized at: ${pickedPrice?.synchronized_at || "N/A"}`;

    const noItems = rates && !rates.length;

    return (
        <Container>
            <FilterSectionFlexboxGrid controlable={!isMobile} style={{ marginBottom: 10 }} centered>
                <StyledItemFitWidth isMobile={isMobile} controlable={!isMobile}>
                    {/* <RatesFiltersContainer> */}
                    <RatesFilters
                        {...{
                            filter,
                            isSupplier,
                            countryList,
                            worldzone_list,
                            subdestination_list,
                            destination_list,
                            pickPrice,
                            pickedPrice,
                            supplierPriceList: filteredSupplierPriceList,
                        }}
                        filteredServicePlanList={filteredPriceList}
                        onChange={onChangeFilters}
                    />
                </StyledItemFitWidth>
                <StyledItemFitWidth isMobile={isMobile} controlable={!isMobile}>
                    <ExportPrice
                        filter={filter}
                        service={service}
                        accountId={accountId}
                        isSupplier={isSupplier}
                        pickedPrice={pickedPrice}
                        disabled={noItems}
                    />
                    {showAddPriceButton && <DropdownAddPrices
                        mobile={isMobile}
                        setShowAddNewRate={setShowAddNewRate}
                        setShowUploadRate={setShowUploadRate}
                        disabled={isSupplier && isGlobalPrice}
                    />}
                    {/* </RatesFiltersContainer> */}

                    <AddNewRate
                        {...{
                            subdestination_list,
                            accountId,
                            isSupplier,
                            e212List,
                            pickedPrice,
                            pickPrice,
                            supplierPriceList: filteredSupplierPriceList,
                        }}
                        show={showAddNewRate}
                        setShow={setShowAddNewRate}
                        updateItems={() => {
                            getItems(filter, null, pickedPrice?.id);
                        }}
                    />
                </StyledItemFitWidth>
                {isSupplier && <StyledItemAutoMargin>
                    <ButtonClose
                        disabled={notDeletablePrice}
                        onClick={() => setShowDeletePriceModal(true)}
                    >
                        Delete price
                    </ButtonClose>

                    <ButtonPrimary onClick={() => setShowAddNewPrice(true)}>
                        Add new price
                    </ButtonPrimary>
                    {pickedPrice?.from_billing && <Whisper
                        trigger="hover"
                        placement="top"
                        speaker={<Tooltip>
                            {synchronizationMessage}
                        </Tooltip>}
                        disabled={!pickedPrice}
                    >
                        <ButtonGhost
                            style={{ marginLeft: 10 }}
                            onClick={() => {
                                syncSupplierPrice(supplierId, pickedPrice?.id)
                                    .then(() => {
                                        Alert.success("Synchronization initiated");
                                    })
                            }}
                        >
                            Synchronize this platform <Icon icon="refresh" />
                        </ButtonGhost>
                    </Whisper>}
                </StyledItemAutoMargin>}
            </FilterSectionFlexboxGrid>
            <RatesTable
                data={rates}
                loading={ratesLoading || loading}
                onChangePerPage={onSavePerPage}
                {...{
                    total,
                    service,
                    perPage,
                    activePage,
                    onChangePage: handleChangePage,
                    onClickDelete,
                    onClickEdit,
                    isSupplier,
                    isSupervisorSms
                }}
                isGlobalPrice={isGlobalPrice}

            />
            <ModalDeletePrice
                show={deleteModal}
                isSupplier={isSupplier}
                onClose={() => {
                    setDeleteModal(false);
                }}
                onSuccess={onDeleteId}
            />

            <Modal
                show={editModal}
                title="Change Rate"
                footer
                onClose={() => {
                    setEditModal(false)
                }}
                onSuccess={onEditId}
                successText="Update"
            >
                <ModalForm
                    disabled={editDisabled}
                    updateFormRef={setFormRef}
                    rate={rateToEdit}
                    isSupplier={isSupplier}
                />
            </Modal>

            {showDeletePriceModal && <ModalConfirm
                show={showDeletePriceModal}
                title="Delete Price"
                onClose={() => {
                    setShowDeletePriceModal(false);
                }}
                onSuccess={onDeletePrice}
            >
                <p>Are you sure you want to delete this price?</p>
            </ModalConfirm>}

            {showUploadRate &&
                <ModalUploadPrice
                    service={service}
                    accountId={accountId}
                    supplierId={supplierId}
                    isSupplier={isSupplier}
                    title={`Upload Price for ${account.name}`}
                    sp_key={filter.sp_key || spKey || defaultSPKey}
                    cur_key={account.cur_key}
                    accountServicePlanList={accountServicePlanList}
                    currency_list={currency_list}
                    paymentTermsList={accountPaymentTermList}
                    supplierPriceList={filteredSupplierPriceList}
                    pickedPrice={pickedPrice}

                    pickPrice={pickPrice}
                    onSuccess={(data) => {
                        setShowUploadRateResult(true);
                        setDataUploadRateResult(data);

                        getItems(filter, null, pickedPrice?.id);
                    }}
                    onClose={() => setShowUploadRate(false)}
                >

                </ModalUploadPrice>
            }

            {showAddNewPrice && <ModalAddNewPrice
                supplierId={supplierId}
                onClose={() => setShowAddNewPrice(false)}
                onSuccess={() => {
                    setShowAddNewPrice(false);
                    getSupplierPriceList(supplierId);
                }}
            />}

            <ModalUploadResult
                show={showUploadRateResult}
                data={dataUploadRateResult}
                isSupplier={isSupplier}
                onClose={() => setShowUploadRateResult(false)}
            />
        </Container>
    );
};

const mapStateToProps = ({ references }) => ({
    defaultSPKey: references.defaultSPKey,
    spKey: references.spKey,
    servicePlanList: references.service_plan_list
});

export default connect(mapStateToProps)(TabRates);

const Container = styled.div`
    margin-top: 30px;
`;

// const RatesFiltersContainer = styled.div`
//     && {
//         display: flex;
//         align-items: center;
//         margin-bottom: 24px;

//         @media (max-width: 767px) {
//             flex-direction: column;
//             align-items: start;

//             .rs-form-group {
//                 margin-bottom: 10px;
//             }

//             .rs-btn-toolbar > .rs-dropdown {
//                 margin-left: 0px !important;
//             }

//             .rs-btn-toolbar {
//                 margin-bottom: 10px;
//             }
//         }
//     }
// `;

const formModel = Schema.Model({
    rate: NumberType().isRequired("Required")
});

const ModalForm = ({ updateFormRef, rate, isSupplier }) => {
    const [withDate, setWithDate] = useState(false);

    return (
        <Form
            layout="inline"
            model={formModel}
            formDefaultValue={rate}
            ref={ref => updateFormRef(ref)}
        >
            <RateField name="rate" label="Rate" />
            <br />
            <Checkbox onChange={(checked) => { setWithDate(checked) }} name={"withDate"}>From date</Checkbox>
            <CustomField
                accepter={DatePicker}
                placeholder="Enter Date"
                name="start_date"
                disabled={!withDate}
                ranges={[
                    {
                        label: "Today",
                        value: new Date()
                    },
                    {
                        label: "Tomorrow",
                        value: new Date().setDate(new Date().getDate() + 1)
                    }
                ]}
                disabledDate={(date) => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    return date < today;
                }}
            />
            <br />
            {!isSupplier
                ? <Checkbox name="update_allocated_numbers">Update for all allocated numbers</Checkbox>
                : <Checkbox name="update_transit_prefixes">Update for all allocated prefixes</Checkbox>
            }
        </Form>
    );
};